import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Container, IconButton, Alert } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import {
  PORTAL_OTP_LENGTH,
  PORTAL_PENDING_PHONE_KEY,
  PORTAL_TEST_OTP,
  PORTAL_VERIFIED_PHONE_KEY,
  readStorage,
  writeStorage,
} from './portalAuth';
import StickyActionBar, { fullScreenHeight } from '../../components/common/StickyActionBar';

const RESEND_SECONDS = 30;

export default function PortalOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const phone: string | null = (location.state as { phone?: string } | null)?.phone || readStorage('session', PORTAL_PENDING_PHONE_KEY);

  const [otp, setOtp] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [resendIn, setResendIn] = useState(RESEND_SECONDS);

  useEffect(() => {
    if (resendIn <= 0) return;
    const timer = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendIn]);

  // Reached directly without entering a number
  if (!phone) return <Navigate to="/portal/login" replace />;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setInfoMessage('');

    if (otp.length !== PORTAL_OTP_LENGTH) {
      setErrorMessage(`Please enter the ${PORTAL_OTP_LENGTH}-digit OTP`);
      return;
    }
    if (otp !== PORTAL_TEST_OTP) {
      setErrorMessage('Incorrect OTP. Please try again.');
      return;
    }

    writeStorage('local', PORTAL_VERIFIED_PHONE_KEY, phone);
    navigate('/portal/home-screen', { replace: true });
  };

  const handleResend = () => {
    setOtp('');
    setErrorMessage('');
    setInfoMessage(`OTP sent again to +91 ${phone}`);
    setResendIn(RESEND_SECONDS);
  };

  return (
    <Box sx={{ ...fullScreenHeight, bgcolor: '#F2F5F1', p: 2, pb: 0, display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="sm" sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Top Bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pt: 2 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ color: '#000', p: 0 }}>
            <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Headings */}
        <Typography sx={{ color: '#0F231A', fontSize: '32px', fontWeight: 700, lineHeight: 1.1, mb: 1, fontFamily: '"Moan Slight", sans-serif', letterSpacing: '-1px' }}>
          Enter the OTP
        </Typography>
        <Typography sx={{ color: '#666', fontSize: '15px', mb: 4, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          Sent to +91 {phone}.{' '}
          <Box component="span" onClick={() => navigate('/portal/login')} sx={{ color: '#0F763F', fontWeight: 700, cursor: 'pointer' }}>
            Change
          </Box>
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, flexGrow: 1 }}>

          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ borderRadius: '12px' }}>
              {errorMessage}
            </Alert>
          )}
          {infoMessage && (
            <Alert severity="success" onClose={() => setInfoMessage('')} sx={{ borderRadius: '12px' }}>
              {infoMessage}
            </Alert>
          )}

          {/* OTP */}
          <Box>
            <Typography sx={{ color: '#000', fontSize: '14px', fontWeight: 700, mb: 1, fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              One-time password
            </Typography>
            <Box sx={{ border: '1px solid #ddd', borderRadius: '12px', bgcolor: '#fff', overflow: 'hidden' }}>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                placeholder={'•'.repeat(PORTAL_OTP_LENGTH)}
                maxLength={PORTAL_OTP_LENGTH}
                value={otp}
                onChange={(e) => {
                  setErrorMessage('');
                  setOtp(e.target.value.replace(/\D/g, '').slice(0, PORTAL_OTP_LENGTH));
                }}
                style={{ width: '100%', border: 'none', padding: '14px 16px', fontSize: '22px', letterSpacing: '10px', textAlign: 'center', outline: 'none', fontFamily: '"Plus Jakarta Sans", sans-serif' }}
              />
            </Box>
            <Typography sx={{ color: '#666', fontSize: '13px', mt: 1.5, textAlign: 'center', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
              {resendIn > 0 ? (
                `Resend OTP in ${resendIn}s`
              ) : (
                <Box component="span" onClick={handleResend} sx={{ color: '#0F763F', fontWeight: 700, cursor: 'pointer' }}>
                  Resend OTP
                </Box>
              )}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <StickyActionBar>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={otp.length !== PORTAL_OTP_LENGTH}
              sx={{
                bgcolor: '#0F763F !important',
                color: '#fff',
                py: 2,
                borderRadius: '16px',
                fontSize: '17px',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: 'none',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                '&:hover': { bgcolor: '#0d6535 !important', boxShadow: 'none' },
                '&:disabled': { bgcolor: '#A5D6B6 !important', color: '#fff' }
              }}
            >
              Verify & continue
            </Button>
          </StickyActionBar>
        </Box>
      </Container>
    </Box>
  );
}
